import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BaseDropdownOption } from '../../../../common/Dropdown';
import { calculateDateRanges } from '../../../../utils/dateUtils';
import { useSelectedMember } from '../../contexts/SelectedMemberContext';
import { useGetSprints } from '../../hooks/useGetSprints';
import { useSprintTimeline } from '../../hooks/useSprintTimeline';

import { AdhocAccordion } from './components/AdhocAccordion';
import { MeetingsAccordion } from './components/MeetingsAccordion';
import { TicketAccordion } from './components/TicketAccordion';
import { TimelineSection } from './components/TimelineSection';
import { TimelineGrid } from './TimelineGrid';

export function SprintTimeline({
  selectedTimePeriod,
}: {
  selectedTimePeriod: BaseDropdownOption;
}): JSX.Element {
  const { selectedMember } = useSelectedMember();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const yourTeamsIndex = pathParts.indexOf('your-teams');
  const teamId =
    yourTeamsIndex !== -1 && pathParts.length > yourTeamsIndex + 1
      ? pathParts[yourTeamsIndex + 1]
      : null;
  const { thisPeriodStart, thisPeriodEnd } = useMemo(
    () => calculateDateRanges(selectedTimePeriod.value),
    [selectedTimePeriod],
  );

  const { data: sprints } = useGetSprints(
    teamId,
    thisPeriodStart,
    thisPeriodEnd,
  );
  const { data: timelineData, isLoading } = useSprintTimeline(
    selectedMember?.id,
    selectedMember?.employeeId,
    sprints?.map((sprint) => sprint.id) || [],
    thisPeriodStart,
    thisPeriodEnd,
    selectedMember?.shadowTo,
  );
  if (!selectedMember || !sprints?.[0]) {
    return <div>No member or sprint selected</div>;
  }

  const isEmpty = !timelineData?.tickets.length;

  return (
    <>
      <TimelineSection
        title="Work Timeline"
        isLoading={isLoading}
        isEmpty={isEmpty}
        emptyMessage="No tickets assigned for this sprint"
      >
        <div className="overflow-x-auto">
          <div className="min-w-full">
            <TimelineGrid
              workingDays={timelineData?.workingDays || []}
              tickets={timelineData?.tickets || []}
              progressData={timelineData?.progressData || {}}
              dayStatus={timelineData?.dayStatus || {}}
              adhocs={timelineData?.groupedAdhocs || {}}
              meetings={timelineData?.groupedMeetings || {}}
            />
          </div>
        </div>
      </TimelineSection>
      <TimelineSection
        title="Detailed Activity"
        isLoading={isLoading}
        isEmpty={isEmpty}
        emptyMessage="No tickets assigned for this sprint"
      >
        <div className="flex flex-col gap-2">
          {timelineData?.groupedTickets.map((ticket) => (
            <TicketAccordion key={ticket.id} ticket={ticket} />
          ))}
          <AdhocAccordion groupedAdhocs={timelineData?.groupedAdhocs || {}} />
          <MeetingsAccordion
            groupedMeetings={timelineData?.groupedMeetings || {}}
          />
        </div>
      </TimelineSection>
    </>
  );
}
